import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { paginate } from './appDetail';
import { DownloadIcon } from './modalIcons';
import './ReleaseHistory.css';

/** Releases per page, once the card is paged at all. */
const PAGE_SIZE = 3;

/*
 * Wide enough for the modal's two-column body — the mirror of the
 * `max-width: 860px` stacked block in `AppDetailModal.css`. The two numbers are
 * one breakpoint and have to move together.
 */
const WIDE_VIEWPORT = '(min-width: 861px)';

/**
 * The release-history card inside the detail modal.
 *
 * The list only grows: every release an app ever shipped stays in
 * `previousVersionInfo`, so left to itself this card ends up taller than the
 * modal and the whole lower half of the panel turns into a long scroll. On the
 * wide layout it is therefore paged, which caps the card's height without
 * hiding anything. The stacked layout keeps the full list — a column of rows is
 * what a narrow screen scrolls through anyway.
 *
 * The page lives here, so the caller resets it by keying this component on the
 * app on show rather than by managing a page of its own.
 */
export default function ReleaseHistory({ releases }) {
  const [page, setPage] = useState(1);
  // Reports false where `matchMedia` is unavailable, which is the safe way
  // round: no measurement means the full list, never an empty page.
  const isWide = useMediaQuery(WIDE_VIEWPORT);

  const paged = isWide && releases.length > PAGE_SIZE;
  const current = paginate(releases, page, PAGE_SIZE);
  const rows = paged ? current.rows : releases;

  // Stepped from the clamped page rather than from state, so a page left behind
  // by a wider viewport cannot send the first Next somewhere unexpected.
  const step = (delta) => () => setPage(current.page + delta);

  return (
    <section className="itcModalCard itcRelease">
      <div className="itcReleaseHead" data-testid="itcReleaseHead">
        <h3 className="itcModalCardTitle">Release History</h3>

        {paged && (
          <div className="itcReleasePager">
            {/* Live, because pressing Prev or Next changes rows a screen reader
                is not looking at; the count is the only thing that says what
                moved. */}
            <p className="itcReleasePagerCount" aria-live="polite">
              Page {current.page} of {current.pageCount}
            </p>
            <div className="itcReleasePagerButtons">
              <button
                type="button"
                className="itcReleasePagerButton"
                onClick={step(-1)}
                disabled={current.page === 1}
              >
                Prev
              </button>
              <button
                type="button"
                className="itcReleasePagerButton"
                onClick={step(1)}
                disabled={current.page === current.pageCount}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={paged ? 'itcReleaseList itcReleaseListPaged' : 'itcReleaseList'}>
        {rows.map((release) => (
          <div className="itcReleaseRow" data-testid="itcReleaseRow" key={release.version}>
            <div>
              <p className="itcReleaseVersion">v{release.version}</p>
              <p className="itcReleaseNote">{release.note}</p>
            </div>
            {release.downloadLink && (
              <a
                className="itcReleaseDownload"
                href={release.downloadLink}
                aria-label={`Download version ${release.version}`}
              >
                <DownloadIcon />
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

ReleaseHistory.propTypes = {
  releases: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string.isRequired,
      note: PropTypes.string,
      downloadLink: PropTypes.string,
    })
  ).isRequired,
};
